import { useEffect, useState } from 'react';
import { Template } from '@/services/templates.service';
import { Reply, Link as LinkIcon, PhoneCall, Copy, FileText, MapPin, Film, ImageIcon } from 'lucide-react';

/**
 * Compact WhatsApp-style bubble shown when hovering a row in the templates
 * list — a lightweight stand-in for the full phone preview in the editor.
 * Rendered position: fixed by the parent; pointer-events disabled so it never
 * traps the cursor.
 */

function substitute(text: string, samples: Record<string, string>): string {
  return (text ?? '')
    .replace(/\\r\\n/g, '\n').replace(/\\n/g, '\n').replace(/\\t/g, '  ')
    .replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (_m, name: string) =>
      samples?.[name] ? samples[name] : `{{${name}}}`);
}

const BTN_ICON: Record<string, React.ReactNode> = {
  QUICK_REPLY: <Reply size={12} />,
  URL: <LinkIcon size={12} />,
  PHONE: <PhoneCall size={12} />,
  COPY_CODE: <Copy size={12} />,
};

export function TemplateHoverCard({ t }: { t: Template }) {
  const samples = t.previewSamples ?? {};
  const body = substitute(t.body, samples);
  const buttons = t.buttons ?? [];
  const isCarousel = t.type === 'CAROUSEL' && (t.cards?.length ?? 0) > 0;

  return (
    <div className="w-[300px] overflow-hidden rounded-xl border border-border bg-surface shadow-elevated">
      {/* WhatsApp wallpaper */}
      <div className="bg-[#E5DDD5] p-3 dark:bg-[#0b141a]">
        <div className="max-w-full rounded-lg rounded-tl-none bg-white p-2 text-[12.5px] leading-snug text-[#111b21] shadow-sm dark:bg-[#202c33] dark:text-[#e9edef]">
          {/* Header */}
          {t.headerType === 'TEXT' && t.headerText && (
            <div className="mb-1 font-semibold">{substitute(t.headerText, samples)}</div>
          )}
          {t.headerType === 'IMAGE' && (
            t.headerImageUrl
              ? <PreviewImage src={t.headerImageUrl} />
              : <div className="mb-1.5 flex h-20 items-center justify-center rounded-md bg-black/10 text-[11px] dark:bg-white/10">Image header</div>
          )}
          {t.headerType === 'VIDEO' && (
            <div className="mb-1.5 flex h-20 items-center justify-center gap-1.5 rounded-md bg-black/70 text-[11px] text-white">
              <Film size={13} /> Video header
            </div>
          )}
          {t.headerType === 'DOCUMENT' && (
            <div className="mb-1.5 flex items-center gap-1.5 rounded-md bg-black/5 px-2 py-1.5 text-[11px] dark:bg-white/10">
              <FileText size={13} /> {t.headerDocumentFilename || 'Document'}
            </div>
          )}
          {t.headerType === 'LOCATION' && (
            <div className="mb-1.5 flex items-center gap-1.5 rounded-md bg-black/5 px-2 py-1.5 text-[11px] dark:bg-white/10">
              <MapPin size={13} /> Location
            </div>
          )}

          {/* Body — clamped so giant templates don't blow up the card */}
          <div className="line-clamp-[8] whitespace-pre-wrap break-words">{body}</div>

          {t.footer && (
            <div className="mt-1 text-[11px] text-[#667781] dark:text-[#8696a0]">{substitute(t.footer, samples)}</div>
          )}
          <div className="mt-0.5 text-right text-[10px] text-[#667781] dark:text-[#8696a0]">10:30 AM</div>
        </div>

        {isCarousel && <div className="mt-2 flex gap-2 overflow-hidden">{t.cards!.slice(0, 2).map((card,index) => <div key={index} className="w-40 shrink-0 overflow-hidden rounded-lg bg-white text-[#111b21] shadow-sm">
          {card.headerType === 'VIDEO' ? <div className="flex h-24 items-center justify-center bg-black/10"><Film size={22} /></div> : <PreviewImage src={card.headerImageUrl} />}
          {card.body && <div className="line-clamp-2 p-2 text-xs">{substitute(card.body,samples)}</div>}
          {!!card.buttons?.length && <div className="border-t border-black/5 p-2 text-center text-xs text-primary">{card.buttons[0].text}</div>}
        </div>)}</div>}

        {/* Buttons — WhatsApp renders them as rows under the bubble */}
        {buttons.length > 0 && (
          <div className="mt-1 space-y-0.5">
            {buttons.slice(0, 3).map((b, i) => (
              <div key={i} className="flex items-center justify-center gap-1.5 rounded-lg bg-white py-1.5 text-[12px] font-medium text-[#00a5f4] shadow-sm dark:bg-[#202c33]">
                {BTN_ICON[b.type] ?? <Reply size={12} />}
                {b.type === 'COPY_CODE' ? 'Copy code' : (b.text || b.type)}
              </div>
            ))}
            {buttons.length > 3 && (
              <div className="text-center text-[10px] text-[#667781]">+{buttons.length - 3} more</div>
            )}
          </div>
        )}
      </div>

      {/* Meta strip */}
      <div className="flex items-center justify-between px-3 py-1.5 text-[10px] text-text-muted">
        <span className="truncate font-medium">{t.name}</span>
        <span className="shrink-0 uppercase">{t.language} · {t.category}</span>
      </div>
    </div>
  );
}

function PreviewImage({src}: {src?: string}) {
  const [failed,setFailed] = useState(false);
  useEffect(() => setFailed(false), [src]);
  return src && !failed ? <img src={src} alt="Template media" onError={() => setFailed(true)} className="h-28 w-full rounded-md object-cover" /> : <div className="flex h-28 flex-col items-center justify-center gap-2 bg-black/5 text-xs text-text-muted"><ImageIcon size={23} /><span>{src ? 'Image unavailable · update media' : 'No image uploaded'}</span></div>;
}
